// A bounded output-energy envelope on the playback AudioContext clock. Keep
// recent, unducked output in the reference: delayed speaker echo must not look
// like a new speaker just because we lowered the output a moment ago.
const WINDOW_SECONDS = 0.02;
type Window = { start: number; end: number; rms: number };

export function createPlaybackReference(context: AudioContext) {
  const windows: Window[] = [];
  const tail = () =>
    Math.max(0, context.outputLatency || 0) + Math.max(0, context.baseLatency || 0) + 0.3;
  function retire() {
    const before = context.currentTime - tail();
    while (windows.length && windows[0].end < before) windows.shift();
  }
  return {
    append(buffer: AudioBuffer, start: number) {
      retire();
      const samples = buffer.getChannelData(0);
      const size = Math.max(1, Math.round(buffer.sampleRate * WINDOW_SECONDS));
      for (let offset = 0; offset < samples.length; offset += size) {
        const end = Math.min(samples.length, offset + size);
        let energy = 0;
        for (let i = offset; i < end; i++) energy += samples[i] ** 2;
        windows.push({
          start: start + offset / buffer.sampleRate,
          end: start + end / buffer.sampleRate,
          rms: Math.sqrt(energy / (end - offset)),
        });
      }
    },
    level() {
      retire();
      let level = 0;
      for (const window of windows) {
        if (window.start > context.currentTime) break;
        level = Math.max(level, window.rms);
      }
      return level;
    },
    clear() {
      windows.length = 0;
    },
  };
}
